import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Modal, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BarChartIcon from "@mui/icons-material/BarChart";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RedeemIcon from "@mui/icons-material/Redeem";
import useIndex from "../../../../../hooks/useIndex";
import { getCountGamesByUser } from "../../../../../data/api/game/GameRoutes";
import validErrors from "../../../../../hooks/validErrors";

/**
 * PlaysStatsModal
 *
 * Props:
 * - open: boolean
 * - onClose: fn
 * - handleOpen: fn  -> abre modal login/register desde el padre
 */
const PlaysStatsModal = ({ open, onClose, handleOpen }) => {
  const { auth, token } = useIndex();

  const [load, setLoad] = useState(false);
  const [dataFound, setDataFound] = useState({
    total_plays: 0,
    available: 0,
    winners: 0,
    gift_total: 0,
  });

  const isLogged = !!auth && !!token;

  const loadData = async () => {
    if (!token) return;
    setLoad(true);
    try {
      const { data } = await getCountGamesByUser(token);
      if (data?.success) {
        setDataFound({
          total_plays: data?.data?.total_plays ?? 0,
          available: data?.data?.available ?? 0,
          winners: data?.data?.winners ?? 0,
          gift_total: data?.data?.gift_total ?? 0,
        });
      }
    } catch (error) {
      validErrors(error);
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    // Si no está logueado, no consultes
    if (!isLogged) return;

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isLogged]);

  return (
    <Modal open={open} onClose={onClose}>
      <Center>
        <Card>
          {/* Header */}
          <Header>
            <TitleWrap>
              <BarChartIcon className="leadIcon" />
              <Typography className="title">Estadísticas de Jugadas</Typography>
            </TitleWrap>

            <IconButton onClick={onClose} className="closeBtn" size="small">
              <CloseIcon />
            </IconButton>
          </Header>

          {/* Body */}
          <Body>
            {!isLogged ? (
              <AuthGate>
                <Typography className="gateTitle">
                  Para conocer tus estadísticas debes iniciar sesión
                </Typography>

                <Typography className="gateText">
                  Inicia sesión para ver tus jugadas, disponibles, ganadores y
                  obsequios.
                </Typography>

                <GateBtn
                  type="button"
                  onClick={() => {
                    onClose?.();
                    handleOpen?.();
                  }}
                >
                  Ingresar
                </GateBtn>
              </AuthGate>
            ) : (
              <>
                {load && (
                  <LoaderWrap>
                    <Spinner />
                    <Typography className="loadingText">
                      Cargando estadísticas...
                    </Typography>
                  </LoaderWrap>
                )}

                <Content dim={load}>
                  {/* <StatCard className="blue">
                    <div className="left">
                      <ConfirmationNumberIcon className="icon" />
                      <span className="label">Total Jugadas</span>
                    </div>
                    <span className="value">{dataFound?.total_plays ?? 0}</span>
                  </StatCard> */}

                  <StatCard className="green">
                    <div className="left">
                      <ConfirmationNumberIcon className="icon" />
                      <span className="label">Disponibles</span>
                    </div>
                    <span className="value">{dataFound?.available ?? 0}</span>
                  </StatCard>

                  <StatCard className="gold">
                    <div className="left">
                      <EmojiEventsIcon className="icon" />
                      <span className="label">Ganador</span>
                    </div>
                    <span className="value">{dataFound?.winners ?? 0}</span>
                  </StatCard>

                  <StatCard className="pink">
                    <div className="left">
                      <RedeemIcon className="icon" />
                      <span className="label">Total jugadas de obsequio</span>
                    </div>
                    <span className="value">{(dataFound?.gift_total + dataFound?.winners) ?? 0}</span>
                  </StatCard>
                </Content>
              </>
            )}
          </Body>
        </Card>
      </Center>
    </Modal>
  );
};

export default PlaysStatsModal;

/* ===================== styles ===================== */

const Center = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
`;

const Card = styled.div`
  width: min(720px, 40vw);
  max-width: 92vw;

  max-height: min(82vh, 760px);
  overflow: hidden;

  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.45);

  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    width: min(720px, 92vw);
  }
`;

const Header = styled.div`
  padding: 18px 18px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;

  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  .closeBtn {
    border: 2px solid rgba(0, 0, 0, 0.25);
    border-radius: 10px;
  }

  @media (max-width: 600px) {
    padding: 16px 14px 12px;
  }
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .leadIcon {
    font-size: clamp(26px, 2.4vw, 34px);
    color: #2f6cff;
    flex: 0 0 auto;
  }

  .title {
    font-weight: 900;
    font-size: clamp(1.25rem, 2.4vw, 2.05rem);
    line-height: 1.05;
  }
`;

const Body = styled.div`
  padding: 18px;
  flex: 1;
  overflow-y: auto;

  display: grid;
  gap: 16px;

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 999px;
  }

  @media (max-width: 600px) {
    padding: 14px;
    gap: 14px;
  }
`;

/* ===== auth gate ===== */

const AuthGate = styled.div`
  min-height: 280px;
  display: grid;
  place-items: center;
  gap: 12px;
  padding: 10px 6px;
  text-align: center;

  .gateTitle {
    font-weight: 900;
    font-size: clamp(1.15rem, 2.2vw, 1.5rem);
    color: #1a1d28;
  }

  .gateText {
    color: rgba(26, 29, 40, 0.72);
    font-weight: 600;
    max-width: 520px;
    font-size: clamp(0.95rem, 1.5vw, 1.05rem);
  }
`;

const GateBtn = styled.button`
  width: min(420px, 100%);
  height: 56px;
  border: 0;
  border-radius: 12px;
  cursor: pointer;

  background: #743fe3;
  color: #ffffff;

  font-weight: 900;
  font-size: 1.15rem;

  box-shadow: 0 14px 22px rgba(0, 0, 0, 0.16);
  transition: filter 0.12s ease, transform 0.12s ease;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

/* ===== loader ===== */

const LoaderWrap = styled.div`
  display: grid;
  place-items: center;
  gap: 10px;
  padding: 10px 0 2px;

  .loadingText {
    color: rgba(26, 29, 40, 0.7);
    font-weight: 800;
    font-size: 0.95rem;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 4px solid rgba(116, 63, 227, 0.25);
  border-top-color: #743fe3;
  animation: ${spin} 0.8s linear infinite;
`;

const Content = styled.div`
  display: grid;
  gap: 16px;
  opacity: ${(p) => (p.dim ? 0.6 : 1)};
  pointer-events: ${(p) => (p.dim ? "none" : "auto")};

  @media (max-width: 600px) {
    gap: 14px;
  }
`;

/* ===== cards ===== */

const StatCard = styled.div`
  min-height: clamp(70px, 7vw, 92px);
  border-radius: 12px;
  padding: clamp(16px, 2.2vw, 22px);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  color: #fff;
  box-shadow: 0 14px 24px rgba(0, 0, 0, 0.12);

  .left {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  .icon {
    font-size: clamp(26px, 2.8vw, 34px);
    opacity: 0.95;
    flex: 0 0 auto;
  }

  .label {
    font-weight: 900;
    font-size: clamp(1.05rem, 2vw, 1.55rem);
    line-height: 1.05;
    white-space: normal;
  }

  .value {
    font-weight: 900;
    font-size: clamp(1.6rem, 3vw, 2.4rem);
    line-height: 1;
    flex: 0 0 auto;
  }

  &.blue {
    background: linear-gradient(90deg, #4b8bff 0%, #2f6cff 100%);
  }
  &.green {
    background: linear-gradient(90deg, #2ecc71 0%, #1db954 100%);
  }
  &.gold {
    background: linear-gradient(90deg, #f2c94c 0%, #c79a10 100%);
  }
  &.pink {
    background: linear-gradient(90deg, #ff5db1 0%, #e83e8c 100%);
  }
`;
